/** Keep the full-screen phone panel modal while nested Radix sheets own their
 * own focus scope. Desktop never calls this boundary. */
export function containMobileFocus(panel: HTMLElement) {
  const isolated = new Map<HTMLElement, boolean>();
  const ancestors: HTMLElement[] = [];
  for (
    let node: HTMLElement | null = panel;
    node?.parentElement;
    node = node.parentElement
  )
    ancestors.push(node.parentElement);
  const isolate = () => {
    for (const parent of ancestors) {
      for (const sibling of parent.children) {
        if (
          !(sibling instanceof HTMLElement) ||
          sibling === panel ||
          sibling.contains(panel)
        )
          continue;
        if (!isolated.has(sibling))
          isolated.set(sibling, sibling.hasAttribute("inert"));
        sibling.setAttribute("inert", "");
      }
    }
  };
  isolate();
  const observer = new MutationObserver(isolate);
  for (const ancestor of ancestors)
    observer.observe(ancestor, { childList: true });
  const overflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  const nestedDialog = () =>
    panel.querySelector('[role="dialog"][aria-modal="true"]');
  const focusable = () =>
    Array.from(
      panel.querySelectorAll<HTMLElement>(
        "button, a[href], input, select, textarea, summary, video[controls], [tabindex]",
      ),
    ).filter((node) => {
      if (
        node.tabIndex < 0 ||
        node.matches(":disabled") ||
        node.closest('[hidden], [inert], [aria-hidden="true"]')
      )
        return false;
      for (
        let ancestor: HTMLElement | null = node;
        ancestor && ancestor !== panel;
        ancestor = ancestor.parentElement
      ) {
        if (
          getComputedStyle(ancestor).display === "none" ||
          getComputedStyle(ancestor).visibility === "hidden"
        )
          return false;
        if (
          ancestor instanceof HTMLDetailsElement &&
          !ancestor.open &&
          !ancestor.querySelector("summary")?.contains(node)
        )
          return false;
      }
      return true;
    });
  const keydown = (event: KeyboardEvent) => {
    if (event.key !== "Tab" || event.defaultPrevented || nestedDialog()) return;
    const candidates = focusable();
    const first = candidates[0];
    const last = candidates.at(-1);
    if (!first || !last) {
      event.preventDefault();
      panel.focus();
    } else if (
      event.shiftKey &&
      (document.activeElement === first || document.activeElement === panel)
    ) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const focusin = (event: FocusEvent) => {
    if (!panel.contains(event.target as Node) && !nestedDialog())
      (focusable()[0] ?? panel).focus();
  };
  document.addEventListener("keydown", keydown);
  document.addEventListener("focusin", focusin);
  return () => {
    observer.disconnect();
    document.removeEventListener("keydown", keydown);
    document.removeEventListener("focusin", focusin);
    for (const [node, wasInert] of isolated) {
      if (!wasInert) node.removeAttribute("inert");
    }
    document.body.style.overflow = overflow;
  };
}
